/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, Feb 2023
 * @flow
 */

import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import classes from './BookingList.module.css';
import { TmsComponents as TmsEnums } from '../../../../enums';
import { getPickerData } from "../../../../components/CountryPicker";
import FireboardUtils from '../../../../util/fireboard-util';
import Button from '../../../../components/Button';
import { Actions, isActionVisible } from '../BookingActions/action-utils';
import { useSelector } from 'react-redux';
import ElapsedTime from '../ElapsedTimer';
import TMSUtils from '../../../../util/tms-util';
import strings from '../../../../globalization';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  let referenceBookingId = null;
  if (list.length > 1) {
    referenceBookingId = endIndex === 0 ? result[1]?.id : result[endIndex - 1]?.id;
  }
  const after = endIndex === 0 ? false : true;

  return {
    list: result,
    isDragged: startIndex !== endIndex,
    after,
    bookingId: list[startIndex]?.id,
    referenceBookingId,
  };
};

const getItemStyle = (isDragging, draggableStyle) => ({
  useSelect: 'none',
  backgroundColor: isDragging ? '#4a4a4a' : '#3a3a3a',
  marginBottom: '0.8vh',
  borderTop: '0.1vh solid gray',
  ...draggableStyle,
});

const getListStyle = isDraggingOver => ({
  backgroundColor: isDraggingOver ? '#3a3a3a' : '#3a3a3a',
  width: '100%',
});

const BookingList = ({
  bookingList,
  openPopup,
  onBookingActionClick,
  onMoveBooking,
  stationMap,
  isEnabledDragDrop,
}) => {
  const [bookings, setBookings] = useState([]);

  const storeInfo = useSelector(state => state.storeInfo);
  const { id: storeId } = storeInfo?.data || {};

  useEffect(() => {
    setBookings(bookingList);
  }, [bookingList]);


  const onDragEnd = result => {
    //dropped outside the list
    if (!result.destination) {
      return;
    }

    const { list, isDragged, after, bookingId, referenceBookingId } = reorder(
      bookings,
      result.source.index,
      result.destination.index
    );

    setBookings(list);

    if (isDragged && referenceBookingId) {
      const payload = {
        storeId,
        bookingId,
        referenceBookingId,
        after,
      };
      onMoveBooking && onMoveBooking(payload);
    }
  };

  return (
    <div className={classes.container}>
      <Header />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
              className={classes.listWrapper}
            >
              <Bookings
                bookings={bookings}
                openPopup={openPopup}
                onBookingActionClick={onBookingActionClick}
                stationMap={stationMap}
                isEnabledDragDrop={isEnabledDragDrop}
              />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );

}

export default BookingList;

const Bookings = ({ bookings, openPopup, onBookingActionClick, stationMap, isEnabledDragDrop }) => {
  const countriesData = getPickerData();

  const getFormattedPhnNum = (booking) => {
    const { phoneNumber, countryCode } = booking;
    let formattedNumber;
    const dialCode = countryCode
      ? countryCode.includes('+')
        ? countryCode
        : `+${countryCode}`
      : '+1';
    const country = countriesData.find(country => country.dialCode == dialCode);
    let iso2 = 'US';
    if (!FireboardUtils.isObjectBlank(country))
      iso2 = (country?.iso2).toUpperCase();
    formattedNumber = FireboardUtils.formattedPhoneNumber(phoneNumber, iso2);
    return formattedNumber;
  };

  const getBookingStatus = statusId => {
    let statusName = '';
    if (statusId === 0) {
      statusName = strings.statusPending;
    } else if (statusId === 7) {
      statusName = strings.statusComplete;
    } else if (statusId === 9) {
      statusName = strings.statusCanceled;
    }
    return statusName;
  };

  const getBookingColor = statusId => {
    let color = 'black';
    if (statusId === 0) {
      color = 'orange';
    } else if (statusId === 7) {
      color = 'green';
    } else if (statusId === 9) {
      color = 'red';
    }
    return color;
  };

  const getStationName = (stationId) => {
    if (stationMap && stationMap.has(stationId)) {
      return stationMap.get(stationId)?.number;
    }
    return null;
  };

  const isActionDisabled = (booking, actionType) => {
    let visible = isActionVisible(booking, actionType);

    const disabled = isEnabledDragDrop
      ? actionType === 'moveWaitlist'
        ? !visible
        : true
      : actionType === 'moveWaitlist'
        ? true
        : !visible;

    return disabled;
  };

  return (
    bookings.map((booking, index) => {
      const elapsedTime = TMSUtils.getElapsedTime(booking.bookingDate);
      return (
      <Draggable key={`${booking.id}`} draggableId={`${booking.id}`} index={index} isDragDisabled={!isEnabledDragDrop || booking.status !== 0}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(
              snapshot.isDragging,
              provided.draggableProps.style
            )}
          >
            <div className={classes.row}>
              <div className={classNames({ [classes.numCol]: true, [classes.largerFont]: true })}>{index + 1}.</div>
              <div className={classes.nameCol}>
                <div className={classes.largerFont}>{booking.name}</div>
                <div className={classNames({ [classes.note]: true, [classes.smallerFont]: true })}>{booking.note}</div>
                <div className={classNames({ [classes.note]: true, [classes.smallerFont]: true })}>{booking.comment}</div>
              </div>
              <div className={classes.phoneCol}>
                <div className={classes.largerFont}>{getFormattedPhnNum(booking)}</div>
                <div className={classes.smallerFont}>{booking.emailAddress}</div>
              </div>
              <div className={classNames({ [classes.partyCol]: true, [classes.largerFont]: true })}>{booking.partySize}</div>
              <div className={classes.statusCol}>
                <div className={classes.largerFont} style={{ color: getBookingColor(booking?.status) }}>
                  {getBookingStatus(booking?.status)}
                </div>
                {booking?.status === 0 ? (<div className={classes.timeContainer}>
                <div className={classes.smallerFont}>
                  {FireboardUtils.getTimeInHrsMins(booking.bookingDate)}
                </div>
                <div className={classes.smallerFont}>{' ( '}</div>
                <ElapsedTime bookingDate={booking?.bookingDate} elapsedTime={elapsedTime}/> 
                <div className={classes.smallerFont}>{' ) '}</div>
                </div>) : null}
                {booking?.status === 7 && getStationName(booking?.stationId) && (
                  <div className={classes.smallerFont} style={{ color: '#41a7f5' }}>#{getStationName(booking.stationId)}</div>
                )}
              </div>
            </div>
            <div className={classes.iconWrapper}>
              {Actions.map(action =>
                <Button
                  key={`id-${action.id}-${action.type}`}
                  className={classes.iconBtn}
                  disabled={isActionDisabled(booking, action.type)}
                  onClick={() => {
                    if (action.type === 'allActions') {
                      openPopup(TmsEnums.tmsBookingActions, booking);
                    } else {
                      onBookingActionClick(action.type, booking);
                    }
                  }}>
                  <img src={action.whiteIcon} alt={'Img'} className={classes.icon} />
                </Button>
              )}
            </div>
          </div>
        )}
      </Draggable>
    )}
    )
  );
};

const Header = () => {
  return (
    <div className={classes.headerRow}>
      <div className={classNames({ [classes.numCol]: true })}>#</div>
      <div className={classNames({ [classes.nameCol]: true })}>{strings.nameLabel}</div>
      <div className={classNames({ [classes.phoneCol]: true })}>{strings.phoneLabel}</div>
      <div className={classNames({ [classes.partyCol]: true })}>{strings.partyLabel}</div>
      <div className={classNames({ [classes.statusCol]: true })}>{strings.statusLabel}</div>
    </div>
  );
};
